import classNames from 'classnames';
import { Observable } from '@reactivex/rxjs';
import { Gestures } from 'rxjs-gestures';
import mapPropsStream from 'react-recompose/mapPropsStream';
import createEventHandler from 'react-recompose/createEventHandler';

import styles from './styles.less';

const DOUBLE_CLICK_TIME = 200;

const withBrushControls = mapPropsStream((props) => {
    const { handler: onBrushTouchStart, stream: brushTouchStarts } = createEventHandler();
    const { handler: onLeftHandleTouchStart, stream: leftHandleTouchStarts } = createEventHandler();
    const { handler: onRightHandleTouchStart, stream: rightHandleTouchStarts } = createEventHandler();
    const brushTaps = Gestures.tap(brushTouchStarts).repeat().mergeAll();
    const brushSingleTaps = brushTaps
        .buffer(brushTaps.debounceTime(DOUBLE_CLICK_TIME))
        .filter(eList => eList.length === 1)
        .map(eList => eList[0]);
    const brushDoubleTaps = brushTaps
        .bufferCount(2, 1)
        .filter(eList => eList.length === 2
            && eList[1].time - eList[0].time < DOUBLE_CLICK_TIME
            && eList[1].clientX - eList[0].clientX < 10
            && eList[1].clientY - eList[0].clientY < 10
        )
        .map(eList => eList[1]);
    const brushDrags = Gestures.pan(brushTouchStarts).repeat().flatMap(mapDrags('pan', 25));
    const leftHandleDrags = Gestures.pan(leftHandleTouchStarts).repeat().flatMap(mapDrags('left'));
    const rightHandleDrags = Gestures.pan(rightHandleTouchStarts).repeat().flatMap(mapDrags('right'));

    return Observable.from(props).publish((props) => props.merge(
        brushSingleTaps.withLatestFrom(props)
            .do(([tap, { timebarRef, timeScale, onTap }]) => {
                const rect = timebarRef.current.getBoundingClientRect();
                const x = tap.clientX - rect.left;
                const t = +timeScale.invert(x);
                onTap(t);
            })
            .ignoreElements(),
        brushDoubleTaps.withLatestFrom(props)
            .filter(() => confirm("Delete interval?"))
            .do(([_, props]) => props.onDoubleTap && props.onDoubleTap({ props }))
            .ignoreElements(),
        brushDrags.withLatestFrom(props, adjustTime),
        leftHandleDrags.withLatestFrom(props, adjustTime),
        rightHandleDrags.withLatestFrom(props, adjustTime)
    )).map((props) => ({
        ...props,
        onBrushTouchStart,
        onLeftHandleTouchStart,
        onRightHandleTouchStart
    }));

    function mapDrags(type, µ) {
        return function(drags) {
            return (µ ? drags.decelerate(µ) : drags)
                .stopPropagation()
                .map((point) => ({ type, point }))
                .concat(Observable.of({ type: 'done', point: {} }))
        }
    }

    function adjustTime({ type, point }, props) {
        const { timeScale, timeDomain, minTime, maxTime, yAxisWidth = 0 } = props;
        let { origin, movementX, movementXTotal } = point;
        let [x0, x1] = timeDomain.map((x) => timeScale(x));
        if (!!movementX) {
            if (!origin.hasOwnProperty('leftHandle')) {
                origin.leftHandle = type === 'left' ? Math.max(x0, timeScale(timeScale.invert(origin.targetX))) : x0;
            }
            if (!origin.hasOwnProperty('rightHandle')) {
                origin.rightHandle = type === 'right' ? Math.min(x1, timeScale(timeScale.invert(origin.targetRight - yAxisWidth))) : x1;
            }
            x0 = origin.leftHandle;
            x1 = origin.rightHandle;
            if (type === 'pan') {
                if (x0 + movementXTotal < timeScale(minTime)) {
                    movementXTotal = timeScale(minTime) - x0;
                } else if (x1 + movementXTotal > timeScale(maxTime)) {
                    movementXTotal = timeScale(maxTime) - x1;
                }
                timeDomain[0] = Math.max(minTime, +timeScale.invert(x0 + movementXTotal));
                timeDomain[1] = Math.min(maxTime, +timeScale.invert(x1 + movementXTotal));
            } else if (type === 'left') {
                timeDomain[0] = Math.max(minTime, +timeScale.invert((x0 + movementXTotal) > x1 ? x1 : (x0 + movementXTotal)));
                timeDomain[1] = Math.min(maxTime, +timeScale.invert((x0 + movementXTotal) > x1 ? (x0 + movementXTotal) : x1));
            } else if (type === 'right') {
                timeDomain[0] = Math.max(minTime, +timeScale.invert((x1 + movementXTotal) > x0 ? x0 : (x1 + movementXTotal)));
                timeDomain[1] = Math.min(maxTime, +timeScale.invert((x1 + movementXTotal) > x0 ? (x1 + movementXTotal) : x0));
            }
        }
        props.onTimeChange && props.onTimeChange({ props, type, timeDomain });
        return {
            ...props,
            timeDomain,
            isPanScrolling: type !== 'done' && (type === 'pan'),
            isPanZooming: type !== 'done' && (type === 'left' || type === 'right'),
        };
    }
});

const defaultHandleStyle = { fill: '#000', fillOpacity: 0, stroke: '#777', cursor: 'ew-resize' };
const defaultBrushStyle = { fill: '#000', fillOpacity: 0, stroke: '#fff', cursor: '-webkit-grab' };

const scrollingHandleStyle = { ...defaultHandleStyle, cursor: '-webkit-grabbing' };
const zoomingBrushStyle = { ...defaultBrushStyle, fillOpacity: 0.1, cursor: 'ew-resize' };
const scrollingBrushStyle = { ...defaultBrushStyle, fillOpacity: 0.1, cursor: '-webkit-grabbing' };

export const Brush = withBrushControls(function renderBrush({
    id,
    timeDomain = [0, 0],
    timeScale = () => 0,
    onMouseMove = () => 0,
    onTouchStart = () => 0,
    isPanZooming = false,
    isPanScrolling = false,
    brushBgStyle,
    leftHandleStyle,
    rightHandleStyle,
    onBrushTouchStart,
    onLeftHandleTouchStart,
    onRightHandleTouchStart,
    mode = 'positive',
    width: chartWidth,
    height,
    showHandleArrow,
    ...props
}) {
    const y = 0;

    const x1 = Math.max(0, timeScale(timeDomain[1]) || 0) + 1;
    const x2 = Math.max(0, Math.min(timeScale(timeDomain[0]) || 0, x1 - 1));

    const dx = Math.abs(x1 - x2);
    const width = Math.max(1, dx || 0);
    const x = Math.min(x1, x2) + ((dx - width) * 0.5);

    const brushBounds = { x, y, width: Math.min(width, chartWidth - x), height: height + 1 };
    const handleStyle = isPanScrolling ? scrollingHandleStyle : defaultHandleStyle;
    const brushStyle = isPanScrolling ? scrollingBrushStyle : isPanZooming ? zoomingBrushStyle : defaultBrushStyle;


    const brushNumber = Number(id.split('brush-')[1]);

    //from pivotapp..
    const colorRemapping = {
        blue: '#256599',
        brown: '#5D7850',
        //gray: '#EDEDED',
        orange: '#FF9D40',
        lightblue: '#40A7FF',
        lightgreen: '#6DB34B',
        pink: '#C55288',
        red: '#E75D51',
        purple: '#A71AD3',
        green: '#268D59',
        darkorange: '#C67600',
        magenta: '#B42852',
        darkblue: '#1B3872',
        darkred: '#760E0E',
        darkgreen: '#1C5437',
        mint: '#84DBAE',
        lightpurple: '#6F45E2'
    };

    const keys = Object.keys(colorRemapping);
    const colorName = keys[brushNumber % keys.length];
    const color = colorRemapping[ colorName ];

    const HANDLE_WIDTH = 5;
    const HANDLE_WIDTH_HALF = HANDLE_WIDTH / 2;
    const handleRightX = Math.min(width, chartWidth - x) - HANDLE_WIDTH;

    return (
        <g 
            id={id} 
            className={
                classNames({
                    [styles['brush']]: true,
                    [styles[mode]]: true,
                    [styles['is-pan-scrolling']]: isPanScrolling,
                    [styles['is-pan-zooming']]: isPanZooming
                }) }
            {...brushBounds} 
            transform={`translate(${brushBounds.x}, ${brushBounds.y})`}
            >
            { 
                mode === 'negative'
                ?   <rect
                        {...{ ...brushBounds, 
                            x: -brushBounds.x,
                            width: brushBounds.x
                        }}
                        key='brush-neg-left-bg'
                        className={styles['bg-left']}
                        onMouseMove={onMouseMove}
                        onTouchMove={onMouseMove}
                        onMouseDown={onBrushTouchStart}
                        onTouchStart={onBrushTouchStart}
                    />
                : null
            }
            { 
                mode === 'negative'
                ?   <rect
                        {...{ ...brushBounds, 
                            x: brushBounds.width,
                            width: Math.max(0, chartWidth - (brushBounds.x + brushBounds.width) - 1)
                        }}
                        key='brush-neg-right-bg'
                        className={styles['bg-right']}
                        onMouseMove={onMouseMove}
                        onTouchMove={onMouseMove}
                        onMouseDown={onBrushTouchStart}
                        onTouchStart={onBrushTouchStart}
                    />
                : null
            }
            <rect
                {...{ ...brushBounds, x: 1 }}
                key='brush-bg'
                style={{ 
                    ...brushStyle, 
                    ...brushBgStyle,
                    ...(mode === 'positive' ? {fill: color} : {})
                }}
                onMouseMove={onMouseMove}
                onTouchMove={onMouseMove}
                onMouseDown={onBrushTouchStart}
                onTouchStart={onBrushTouchStart}
            />
            <rect
                key='brush-left-handle'
                id='brush-left-handle'
                className={styles['handle']}
                data-handle='left'
                onMouseDown={onLeftHandleTouchStart}
                onTouchStart={onLeftHandleTouchStart}
                {...{ ...brushBounds, width: HANDLE_WIDTH, x: 0 }}
                />
            <polygon
                key='brush-left-arrow'
                id='brush-left-arrow'
                className={classNames(
                    styles['handle'],
                    styles['arrow'],
                    !showHandleArrow && styles['disabled'],
                )}
                data-handle='left'
                onMouseDown={onLeftHandleTouchStart}
                onTouchStart={onLeftHandleTouchStart}
                points={`${HANDLE_WIDTH_HALF},15 ${HANDLE_WIDTH_HALF - 7},0 ${HANDLE_WIDTH_HALF + 7},0`}
                {...{ ...brushBounds }}
            />
            <rect
                key='brush-right-handle'
                id='brush-right-handle'
                className={styles['handle']}
                data-handle='right'
                onMouseDown={onRightHandleTouchStart}
                onTouchStart={onRightHandleTouchStart}
                {...{ ...brushBounds, width: HANDLE_WIDTH, x: handleRightX }}
            />
            <polygon
                key='brush-right-arrow'
                id='brush-right-arrow'
                className={classNames(
                    styles['handle'],
                    styles['arrow'],
                    !showHandleArrow && styles['disabled'],
                )}
                data-handle='right'
                onMouseDown={onRightHandleTouchStart}
                onTouchStart={onRightHandleTouchStart}
                points={
                    `${handleRightX + HANDLE_WIDTH_HALF},15`
                        + ` ${handleRightX + HANDLE_WIDTH_HALF - 7},0`
                        + ` ${handleRightX + HANDLE_WIDTH_HALF + 7},0`
                }
                {...{ ...brushBounds } }
            />
        </g>
    );
});
